import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authPost } from '../api';
import { useAuth } from '../context/AuthContext';
import { APP_VERSION } from '../components/Layout';

// การ์ดเดียว 2 ขั้น: รหัสผ่านผ่านก่อน (server ตอบ next=2fa + cookie pending_2fa) แล้วค่อยถามรหัส 6 หลัก
// ในการ์ดเดิม — wordmark แสดงครั้งเดียว ไม่ซ้ำในขั้นที่ 2 (v2.0.5: เอาบรรทัด tagline ใต้ชื่อระบบออกตามที่ผู้ใช้สั่ง)
const DIGITS = 6;

export default function Login({ initialStep = 1 }) {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(initialStep);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [digits, setDigits] = useState(Array(DIGITS).fill(''));
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const otpRefs = useRef([]);
  const userRef = useRef(null);
  const passRef = useRef(null);
  const backupRef = useRef(null);

  useEffect(() => {
    if (step === 1) (username ? passRef : userRef).current?.focus();
    else if (useBackup) backupRef.current?.focus();
    else otpRefs.current[0]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, useBackup]);

  const finish = async () => {
    await refresh();
    navigate('/dashboard', { replace: true });
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) { setError('กรอกชื่อผู้ใช้และรหัสผ่าน'); return; }
    setBusy(true);
    setError(null);
    try {
      const { status, data } = await authPost('/api/auth/login', { username: username.trim(), password });
      if (status === 200 && data.ok) {
        if (data.next === '2fa') {
          setDigits(Array(DIGITS).fill(''));
          setBackupCode('');
          setUseBackup(false);
          setStep(2);
        } else {
          await finish();
        }
      } else if (status === 401) {
        setError('ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง');
      } else {
        setError(data.error || `เข้าสู่ระบบไม่สำเร็จ (HTTP ${status})`);
      }
    } catch (err) {
      setError('ติดต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (code) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { status, data } = await authPost('/api/auth/2fa', { code });
      if (status === 200 && data.ok) { await finish(); return; }
      if (status === 401 && /expired/i.test(data.error || '')) {
        setStep(1);
        setPassword('');
        setError('หมดเวลายืนยันตัวตน (5 นาที) เข้าสู่ระบบใหม่อีกครั้ง');
        return;
      }
      setError(status === 429
        ? 'กรอกรหัสผิดหลายครั้ง รอ 60 วินาทีแล้วลองใหม่'
        : (useBackup ? 'รหัสสำรองไม่ถูกต้องหรือถูกใช้ไปแล้ว' : 'รหัสไม่ถูกต้อง ตรวจเวลาบนมือถือแล้วลองรหัสใหม่'));
      setDigits(Array(DIGITS).fill(''));
      setBackupCode('');
      setTimeout(() => (useBackup ? backupRef.current : otpRefs.current[0])?.focus(), 0);
    } catch (err) {
      setError('ติดต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง');
    } finally {
      setBusy(false);
    }
  };

  const setDigitsAndMaybeSubmit = (next) => {
    setDigits(next);
    if (next.every((d) => d)) submitCode(next.join(''));
  };

  const onDigit = (i, raw) => {
    const v = raw.replace(/\D/g, '');
    if (v.length > 1) { fillFrom(i, v); return; }
    const next = [...digits];
    next[i] = v;
    if (v && i < DIGITS - 1) otpRefs.current[i + 1]?.focus();
    setDigitsAndMaybeSubmit(next);
  };

  const fillFrom = (start, text) => {
    const next = [...digits];
    text.split('').slice(0, DIGITS - start).forEach((c, k) => { next[start + k] = c; });
    const firstEmpty = next.findIndex((d) => !d);
    otpRefs.current[firstEmpty === -1 ? DIGITS - 1 : firstEmpty]?.focus();
    setDigitsAndMaybeSubmit(next);
  };

  const onKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) otpRefs.current[i - 1]?.focus();
    else if (e.key === 'ArrowLeft' && i > 0) otpRefs.current[i - 1]?.focus();
    else if (e.key === 'ArrowRight' && i < DIGITS - 1) otpRefs.current[i + 1]?.focus();
  };

  const onPaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '');
    if (!text) return;
    e.preventDefault();
    fillFrom(0, text);
  };

  const backToPassword = () => {
    setStep(1);
    setPassword('');
    setError(null);
  };

  return (
    <div className="login-wrap">
      <div className="login-card">
        <span className="wordmark">TTNBCP</span>

        {step === 1 ? (
          <form onSubmit={submitPassword} noValidate>
            {error && <div className="alert alert-error">{error}</div>}
            <label className="lbl" htmlFor="login-user">ชื่อผู้ใช้</label>
            <input ref={userRef} className="input" id="login-user" autoComplete="username"
              value={username} onChange={(e) => setUsername(e.target.value)} />
            <label className="lbl" htmlFor="login-pass">รหัสผ่าน</label>
            <input ref={passRef} className="input" id="login-pass" type="password" autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)} />
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? 'กำลังตรวจสอบ…' : 'เข้าสู่ระบบ'}
            </button>
          </form>
        ) : (
          <form onSubmit={(e) => {
            e.preventDefault();
            const code = useBackup ? backupCode.trim() : digits.join('');
            if (useBackup ? code.length < 8 : code.length < DIGITS) {
              setError(useBackup ? 'รหัสสำรองมี 8 หลัก' : 'กรอกรหัสให้ครบ 6 หลัก');
              return;
            }
            submitCode(code);
          }}>
            {error && <div className="alert alert-error">{error}</div>}
            <p style={{ fontSize: 14, color: 'var(--text-2)', marginTop: error ? 10 : 0 }}>
              {useBackup
                ? 'กรอกรหัสสำรอง 8 หลักที่ได้ตอนเปิดใช้ (ใช้ได้รหัสละครั้ง)'
                : <>กรอกรหัส 6 หลักจากแอป Authenticator{username && <> ของ <b style={{ color: 'var(--ink)' }}>{username.trim()}</b></>}</>}
            </p>
            {useBackup ? (
              <input ref={backupRef} className="input mono" style={{ marginTop: 10, textAlign: 'center', letterSpacing: '.3em', fontSize: 18 }}
                inputMode="numeric" maxLength={8} autoComplete="one-time-code" aria-label="รหัสสำรอง"
                value={backupCode} onChange={(e) => setBackupCode(e.target.value.replace(/\D/g, ''))} />
            ) : (
              <div className="otp" onPaste={onPaste}>
                {digits.map((d, i) => (
                  <input key={i} ref={(el) => { otpRefs.current[i] = el; }} value={d}
                    inputMode="numeric" maxLength={DIGITS} autoComplete={i === 0 ? 'one-time-code' : 'off'}
                    aria-label={`หลักที่ ${i + 1}`} disabled={busy}
                    onChange={(e) => onDigit(i, e.target.value)} onKeyDown={(e) => onKeyDown(i, e)}
                    onFocus={(e) => e.target.select()} />
                ))}
              </div>
            )}
            <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'กำลังตรวจสอบ…' : 'ยืนยัน'}</button>
            <div className="login-foot">
              <button type="button" onClick={() => { setUseBackup(!useBackup); setError(null); }}>
                {useBackup ? 'ใช้รหัสจากแอป' : 'ใช้รหัสสำรอง'}
              </button>
              <button type="button" onClick={backToPassword}>ใช้บัญชีอื่น</button>
            </div>
          </form>
        )}

        <div className="login-foot">TTNBCP v{APP_VERSION}</div>
      </div>
    </div>
  );
}
